import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { MessageCircle, Phone, CheckCircle, Clock, MapPin } from "lucide-react";
import {
  getWhatsAppUrl, PHONE_PRIMARY, PHONE_PRIMARY_TEL, CLINIC_TIMINGS,
  TREATMENT_OPTIONS, PREFERRED_TIME_OPTIONS, CONSENT_TEXT, PRIVACY_LINE, CLINIC_ADDRESS,
  GOOGLE_MAPS_DIRECTIONS, WHATSAPP_BOOKING_INTRO,
} from "@/lib/constants";
import { track, trackCall, trackWhatsApp, trackDirections, trackFormSubmit } from "@/lib/analytics";
import Breadcrumbs from "@/components/Breadcrumbs";

const BookAppointment = () => {
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", phone: "", email: "", service: "", time: "", message: "", consent: false, honeypot: "" });
  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => { track("book_appointment_view"); }, []);

  const validate = () => {
    const e: Record<string, string> = {};
    if (!form.name.trim()) e.name = "Name is required";
    if (!form.phone.trim()) e.phone = "Mobile number is required";
    else if (!/^[6-9]\d{9}$/.test(form.phone.replace(/\s+/g, ""))) e.phone = "Enter a valid 10-digit Indian mobile number";
    if (form.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) e.email = "Enter valid email";
    if (!form.service) e.service = "Please select the treatment you need";
    if (!form.consent) e.consent = "Please tick the consent box so we can contact you";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (form.honeypot) return;
    if (!validate()) return;
    trackFormSubmit("book_appointment_page", form.service);
    const msg = `${WHATSAPP_BOOKING_INTRO} ${form.service}.\nName: ${form.name}\nMobile: ${form.phone}${form.time ? `\nPreferred time: ${form.time}` : ""}${form.message ? `\nMessage: ${form.message}` : ""}\n(Source: Book Appointment page)`;
    window.open(getWhatsAppUrl(msg), "_blank", "noopener,noreferrer");
    navigate("/thank-you");
  };

  return (
    <div>
      <section className="bg-secondary py-12 md:py-16">
        <div className="container">
          <Breadcrumbs items={[{ label: "Book Appointment" }]} />
          <h1 className="font-heading text-3xl md:text-4xl font-bold text-secondary-foreground mb-3">Book Your Consultation in Kalyan</h1>
          <p className="text-secondary-foreground/80">Name and mobile number are all we need — our team will call or WhatsApp you to confirm.</p>
        </div>
      </section>

      <section className="py-16">
        <div className="container max-w-4xl">
          <div className="grid md:grid-cols-3 gap-8">
            <div className="md:col-span-2">
              <form onSubmit={handleSubmit} className="space-y-4" noValidate>
                <input type="text" name="website" className="hidden" tabIndex={-1} autoComplete="off" value={form.honeypot} onChange={e => setForm(f => ({ ...f, honeypot: e.target.value }))} />
                <div>
                  <Label htmlFor="name">Full Name *</Label>
                  <Input id="name" className="h-12" autoComplete="name" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} maxLength={100} />
                  {errors.name && <p className="text-destructive text-xs mt-1">{errors.name}</p>}
                </div>
                <div>
                  <Label htmlFor="phone">Mobile Number *</Label>
                  <Input id="phone" className="h-12" type="tel" inputMode="numeric" autoComplete="tel" pattern="[0-9]{10}" maxLength={10} value={form.phone} onChange={e => setForm(f => ({ ...f, phone: e.target.value.replace(/\D/g, "").slice(0, 10) }))} />
                  {errors.phone && <p className="text-destructive text-xs mt-1">{errors.phone}</p>}
                </div>
                <div>
                  <Label>Treatment Needed *</Label>
                  <Select value={form.service} onValueChange={v => setForm(f => ({ ...f, service: v }))}>
                    <SelectTrigger className="h-12"><SelectValue placeholder="Select your concern" /></SelectTrigger>
                    <SelectContent>
                      {TREATMENT_OPTIONS.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  {errors.service && <p className="text-destructive text-xs mt-1">{errors.service}</p>}
                </div>
                <div>
                  <Label>Preferred Time</Label>
                  <Select value={form.time} onValueChange={v => setForm(f => ({ ...f, time: v }))}>
                    <SelectTrigger className="h-12"><SelectValue placeholder="Morning, afternoon or evening" /></SelectTrigger>
                    <SelectContent>
                      {PREFERRED_TIME_OPTIONS.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="message">Tell us your concern (optional)</Label>
                  <Textarea id="message" value={form.message} onChange={e => setForm(f => ({ ...f, message: e.target.value }))} maxLength={1000} rows={3} />
                </div>
                <div>
                  <Label htmlFor="email">Email (optional)</Label>
                  <Input id="email" className="h-12" type="email" autoComplete="email" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} maxLength={255} />
                  {errors.email && <p className="text-destructive text-xs mt-1">{errors.email}</p>}
                </div>
                <div className="flex items-start gap-3">
                  <Checkbox id="consent" checked={form.consent} onCheckedChange={v => setForm(f => ({ ...f, consent: v === true }))} className="mt-1" />
                  <Label htmlFor="consent" className="text-sm font-normal leading-relaxed">{CONSENT_TEXT} *</Label>
                </div>
                {errors.consent && <p className="text-destructive text-xs">{errors.consent}</p>}
                <Button type="submit" size="lg" className="w-full h-12">Book My Consultation</Button>
                <p className="text-xs text-muted-foreground text-center">{PRIVACY_LINE}</p>
              </form>
            </div>

            <div className="space-y-6">
              <div className="p-5 bg-accent/50 rounded-xl">
                <h2 className="font-heading font-semibold mb-3 text-base">Prefer Instant Booking?</h2>
                <div className="space-y-3">
                  <Button asChild className="w-full h-12" variant="default">
                    <a href={getWhatsAppUrl(WHATSAPP_BOOKING_INTRO)} target="_blank" rel="noopener noreferrer" onClick={() => trackWhatsApp("book_page")}>
                      <MessageCircle className="h-4 w-4 mr-2" /> WhatsApp Us
                    </a>
                  </Button>
                  <Button asChild className="w-full h-12" variant="outline">
                    <a href={PHONE_PRIMARY_TEL} onClick={() => trackCall("book_page")}><Phone className="h-4 w-4 mr-2" /> {PHONE_PRIMARY}</a>
                  </Button>
                </div>
              </div>

              <div className="space-y-2">
                {["No advance payment", "Friendly consultation", "No long wait times"].map(t => (
                  <div key={t} className="flex items-center gap-2 text-sm">
                    <CheckCircle className="h-4 w-4 text-primary" />
                    <span>{t}</span>
                  </div>
                ))}
              </div>

              <div className="p-5 bg-accent/50 rounded-xl">
                <h2 className="font-heading font-semibold text-sm mb-2 flex items-center gap-2">
                  <Clock className="h-4 w-4 text-primary" /> Clinic Timings
                </h2>
                <div className="text-xs text-muted-foreground space-y-1">
                  <p>Mon-Sat: {CLINIC_TIMINGS.weekdays}</p>
                  <p>Sunday: {CLINIC_TIMINGS.sunday}</p>
                  <p>Closed: {CLINIC_TIMINGS.closed}</p>
                </div>
                <a
                  href={GOOGLE_MAPS_DIRECTIONS}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => trackDirections("book_page")}
                  className="mt-3 inline-flex items-start gap-2 text-xs text-primary hover:underline"
                >
                  <MapPin className="h-4 w-4 shrink-0" /> {CLINIC_ADDRESS} — Get directions
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default BookAppointment;
